import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { useAuth } from "./AuthContext";
import { Modal, Button } from "../ui";
// amountToRelease / gst / processingCharge store PAISE.
import { toRupees, toPaise } from "../../utils/money";

const EditBusinessDetailsModal = ({ show, onHide, item, onSave }) => {
  const { jwt } = useAuth();
  
  const [formData, setFormData] = useState({
    amountToRelease: "",
    dataEntryOperatorStatus: "",
    branchManagerStatus: "",
    adminStatus: "",
  });
  
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasLoanStatements, setHasLoanStatements] = useState(false);
  const [checkingStatements, setCheckingStatements] = useState(false);

  useEffect(() => {
    if (show && item) {
      setFormData({
        // PAISE -> RUPEES for the input box. The operator types rupees, so the
        // field must show rupees; handleSubmit converts back with toPaise.
        // Loading paise here and saving it unchanged looks harmless but is not:
        // it means every save through this modal has to be the exact value that
        // was loaded, and any correction the operator types is interpreted as
        // paise and divides the loan by 100.
        amountToRelease: item.amountToRelease ? toRupees(item.amountToRelease) : "",
        dataEntryOperatorStatus: item.dataEntryOperatorStatus || "pending",
        branchManagerStatus: item.branchManagerStatus || "pending",
        adminStatus: item.adminStatus || "pending",
      });
      setError("");
      checkLoanStatements(item.id);
    }
  }, [show, item]);

  const checkLoanStatements = async (memberId) => {
    setCheckingStatements(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/getModelData/loanstatement?memberId=${memberId}`);
      if (res.data && res.data.data && res.data.data.length > 0) {
        setHasLoanStatements(true);
      } else {
        setHasLoanStatements(false);
      }
    } catch (err) {
      console.error("Error checking loan statements", err);
    } finally {
      setCheckingStatements(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setError("");
      
      // Validation
      if (Number(formData.amountToRelease) <= 0) {
        setError("Amount To Release must be greater than 0");
        return;
      }
      if (!formData.dataEntryOperatorStatus || !formData.branchManagerStatus || !formData.adminStatus) {
        setError("All status fields are required.");
        return;
      }

      setLoading(true);
      
      const payload = {
        memberId: item.id,
        // RUPEES -> PAISE. The field holds rupees (see the toRupees on load);
        // the column is paise. This is the write half of the pair and the
        // dangerous one — a display bug misleads, this would corrupt the loan.
        amountToRelease: toPaise(formData.amountToRelease),
        dataEntryOperatorStatus: formData.dataEntryOperatorStatus,
        branchManagerStatus: formData.branchManagerStatus,
        adminStatus: formData.adminStatus
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/superadmin/editBusinessDetails`,
        payload,
        {
          headers: {
            "x-token": jwt
          }
        }
      );

      if (response.data) {
        onSave(); // Refresh data and close modal
      }
    } catch (err) {
      console.error("Error saving business details:", err);
      setError(err.response?.data?.error || "Failed to save business details.");
    } finally {
      setLoading(false);
    }
  };

  if (!item) return null;

  return (
    <Modal
      isOpen={show}
      onClose={onHide}
      title="Edit Business Details"
      size="lg"
    >
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 p-3.5 rounded-xl text-xs font-semibold animate-pulse">
            {error}
          </div>
        )}
        
        {hasLoanStatements && !checkingStatements && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3.5 rounded-xl text-xs font-semibold">
            <span className="font-bold">Warning:</span> Loan statements already exist. Changing Amount To Release updates member details only and will not alter existing EMI schedules.
          </div>
        )}

        {/* Read-Only Information */}
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
          <h4 className="text-xs font-bold text-ink-400 uppercase tracking-wider mb-3 pb-1.5 border-b border-slate-200/60">
            Read-Only Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
            <div className="space-y-1">
              <p className="text-ink-600">
                <span className="font-bold text-ink-500 mr-1">Customer Name:</span>
                <span className="font-medium text-ink-900">{item.fk_member_details_belongsTo_personal_details_customerId?.name || "N/A"}</span>
              </p>
              <p className="text-ink-600">
                <span className="font-bold text-ink-500 mr-1">Customer ID:</span>
                <span className="font-semibold text-primary-600">{item.fk_member_details_belongsTo_personal_details_customerId?.customerUniqueId || "N/A"}</span>
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-ink-600">
                <span className="font-bold text-ink-500 mr-1">Application ID:</span>
                <span className="font-medium text-ink-900">{item.ApplicationId || "N/A"}</span>
              </p>
              <p className="text-ink-600">
                <span className="font-bold text-ink-500 mr-1">Loan ID:</span>
                <span className="font-medium text-ink-900">{item.loanId || "N/A"}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Editable Details Form */}
        <div className="space-y-4">
          <h4 className="text-xs font-bold text-primary-600 uppercase tracking-wider pb-1.5 border-b border-primary-100">
            Editable Details
          </h4>
          
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5">
              Amount To Release <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="amountToRelease"
              value={formData.amountToRelease}
              onChange={handleChange}
              disabled={checkingStatements}
              className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-white text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:bg-slate-50 disabled:text-ink-400"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5">
                Data Entry Operator Status
              </label>
              <select
                name="dataEntryOperatorStatus"
                value={formData.dataEntryOperatorStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-white text-ink-900 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="retrigger">Retrigger</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5">
                Branch Manager Status
              </label>
              <select
                name="branchManagerStatus"
                value={formData.branchManagerStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-white text-ink-900 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="retrigger">Retrigger</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5">
                Admin Status
              </label>
              <select
                name="adminStatus"
                value={formData.adminStatus}
                onChange={handleChange}
                className="w-full px-3 py-2 text-xs border border-border rounded-lg bg-white text-ink-900 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
              >
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Modal Footer Buttons */}
        <div className="flex justify-end items-center gap-3 pt-4 border-t border-slate-100">
          <Button
            variant="secondary"
            onClick={onHide}
            disabled={loading}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || checkingStatements}
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditBusinessDetailsModal;
